import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";

import {
    ActionIcon,
    Button,
    Card,
    Center,
    Group,
    Loader,
    Modal,
    NumberInput,
    Select,
    Stack,
    Switch,
    Table,
    Text,
    TextInput,
    Title,
} from "@mantine/core";

import { notifications } from "@mantine/notifications";

import {
    IconEdit,
    IconPlus,
    IconRefresh,
    IconTrash,
} from "@tabler/icons-react";

import {
    createFoodMenuItem,
    deleteFoodMenuItem,
    getAdminFoodMenu,
    getFoodEstimateSettings,
    setFoodMenuItemActive,
    updateFoodEstimateSettings,
    updateFoodMenuItem,
} from "../../../api/foodApi";

import type {
    EstimateSettings,
} from "./foodEstimate";

import type { FoodMenuItem } from "./foodTypes";

interface ProductForm {
    name: string;
    category: string;
    price: number;
    imageUrl: string;
    sortOrder: number;
    isActive: boolean;
}

const emptyForm: ProductForm = {
    name: "",
    category: "Mad",
    price: 0,
    imageUrl: "",
    sortOrder: 0,
    isActive: true,
};

export default function FoodMenuAdminPage() {
    const { eventId } = useParams<{ eventId: string }>();

    const [estimateSettings, setEstimateSettings] =
        useState<EstimateSettings>({
            microwaveCount: 4,
            minutesPerBox: 7,
        });

    const parsedEventId = Number(eventId);

    const [items, setItems] =
        useState<FoodMenuItem[]>([]);

    const [loading, setLoading] =
        useState(true);

    const [saving, setSaving] =
        useState(false);

    const [savingSettings, setSavingSettings] =
        useState(false);

    const [modalOpen, setModalOpen] =
        useState(false);

    const [editingItem, setEditingItem] =
        useState<FoodMenuItem | null>(null);

    const [form, setForm] =
        useState<ProductForm>(emptyForm);

    // ---------------------------------------------------------
    // LOAD PRODUCTS
    // ---------------------------------------------------------

    async function loadEstimateSettings() {
        try {
            const data =
                await getFoodEstimateSettings(
                    parsedEventId
                );

            setEstimateSettings({
                microwaveCount:
                    data.microwaveCount,
                minutesPerBox:
                    data.minutesPerBox,
            });
        } catch (error) {
            console.error(error);

            notifications.show({
                color: "red",
                title: "Fejl",
                message:
                    "Kunne ikke hente køkkenindstillinger.",
            });
        }
    }

    async function loadItems() {
        if (
            !Number.isFinite(parsedEventId) ||
            parsedEventId <= 0
        ) {
            setItems([]);
            setLoading(false);

            return;
        }

        setLoading(true);

        try {
            const data =
                await getAdminFoodMenu(
                    parsedEventId
                );

            const safeData =
                Array.isArray(data)
                    ? data
                    : (data as any)?.$values ?? [];

            setItems(safeData);

        } catch (error) {
            console.error(error);

            notifications.show({
                color: "red",
                title: "Fejl",
                message:
                    "Kunne ikke hente produkter.",
            });

            setItems([]);

        } finally {
            setLoading(false);
        }
    }

    useEffect(() => {
        loadItems();
        loadEstimateSettings();

        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [parsedEventId]);

    // ---------------------------------------------------------
    // CREATE
    // ---------------------------------------------------------

    async function saveEstimateSettings() {
        if (
            !Number.isFinite(parsedEventId) ||
            parsedEventId <= 0
        ) {
            return;
        }

        if (
            estimateSettings.microwaveCount < 1 ||
            estimateSettings.minutesPerBox < 0
        ) {
            notifications.show({
                color: "red",
                title: "Ugyldige indstillinger",
                message:
                    "Antal mikroovne skal være mindst 1, og minutter pr. boks må ikke være negativ.",
            });

            return;
        }

        setSavingSettings(true);

        try {
            const data =
                await updateFoodEstimateSettings(
                    parsedEventId,
                    estimateSettings
                );

            setEstimateSettings(data);

            notifications.show({
                color: "green",
                title: "Gemt",
                message:
                    "Køkkenindstillingerne blev gemt.",
            });
        } catch (error) {
            console.error(error);

            notifications.show({
                color: "red",
                title: "Fejl",
                message:
                    error instanceof Error
                        ? error.message
                        : "Kunne ikke gemme køkkenindstillingerne.",
            });
        } finally {
            setSavingSettings(false);
        }
    }

    function openCreate() {
        const nextSortOrder =
            items.length === 0
                ? 1
                : Math.max(
                    ...items.map(
                        x => x.sortOrder
                    )
                ) + 1;

        setEditingItem(null);

        setForm({
            ...emptyForm,
            sortOrder:
                nextSortOrder,
        });

        setModalOpen(true);
    }

    // ---------------------------------------------------------
    // EDIT
    // ---------------------------------------------------------

    function openEdit(
        item: FoodMenuItem
    ) {
        setEditingItem(item);

        setForm({
            name:
                item.name,

            category:
                item.category,

            price:
                item.price,

            imageUrl:
                item.imageUrl ?? "",

            sortOrder:
                item.sortOrder,

            isActive:
                item.isActive,
        });

        setModalOpen(true);
    }

    // ---------------------------------------------------------
    // SAVE
    // ---------------------------------------------------------

    async function saveProduct() {
        if (
            !Number.isFinite(parsedEventId) ||
            parsedEventId <= 0
        ) {
            notifications.show({
                color: "red",
                title: "Fejl",
                message:
                    "Event ID mangler.",
            });

            return;
        }

        if (!form.name.trim()) {
            notifications.show({
                color: "red",
                title: "Mangler navn",
                message:
                    "Produktnavn skal udfyldes.",
            });

            return;
        }

        if (form.price < 0) {
            notifications.show({
                color: "red",
                title: "Forkert pris",
                message:
                    "Prisen må ikke være negativ.",
            });

            return;
        }

        setSaving(true);

        try {
            if (editingItem) {
                await updateFoodMenuItem(
                    parsedEventId,
                    editingItem.id,
                    {
                        name:
                            form.name.trim(),

                        category:
                            form.category,

                        price:
                            form.price,

                        imageUrl:
                            form.imageUrl.trim(),

                        sortOrder:
                            form.sortOrder,

                        isActive:
                            form.isActive,
                    }
                );

                notifications.show({
                    color: "green",
                    title: "Gemt",
                    message:
                        `${form.name} blev opdateret.`,
                });

            } else {
                await createFoodMenuItem(
                    parsedEventId,
                    {
                        name:
                            form.name.trim(),

                        category:
                            form.category,

                        price:
                            form.price,

                        imageUrl:
                            form.imageUrl.trim(),

                        sortOrder:
                            form.sortOrder,
                    }
                );

                notifications.show({
                    color: "green",
                    title: "Oprettet",
                    message:
                        `${form.name} blev oprettet.`,
                });
            }

            setModalOpen(false);
            setEditingItem(null);
            setForm(emptyForm);

            await loadItems();

        } catch (error) {
            console.error(error);

            notifications.show({
                color: "red",
                title: "Fejl",
                message:
                    error instanceof Error
                        ? error.message
                        : "Kunne ikke gemme produktet.",
            });

        } finally {
            setSaving(false);
        }
    }

    // ---------------------------------------------------------
    // ACTIVE
    // ---------------------------------------------------------

    async function toggleActive(
        item: FoodMenuItem
    ) {
        const active =
            !item.isActive;

        try {
            await setFoodMenuItemActive(
                parsedEventId,
                item.id,
                active
            );

            setItems(prev =>
                prev.map(x =>
                    x.id === item.id
                        ? {
                            ...x,
                            isActive:
                                active,
                        }
                        : x
                )
            );

            notifications.show({
                color: "green",

                title:
                    active
                        ? "Aktiveret"
                        : "Deaktiveret",

                message:
                    active
                        ? `${item.name} vises nu på bestillingssiden.`
                        : `${item.name} er skjult fra bestillingssiden.`,
            });

        } catch (error) {
            console.error(error);

            notifications.show({
                color: "red",
                title: "Fejl",
                message:
                    "Kunne ikke ændre produktets status.",
            });
        }
    }

    // ---------------------------------------------------------
    // DELETE
    // ---------------------------------------------------------

    async function deleteProduct(
        item: FoodMenuItem
    ) {
        const confirmed =
            window.confirm(
                `Vil du virkelig slette "${item.name}"?`
            );

        if (!confirmed)
            return;

        try {
            await deleteFoodMenuItem(
                parsedEventId,
                item.id
            );

            setItems(prev =>
                prev.filter(
                    x =>
                        x.id !==
                        item.id
                )
            );

            notifications.show({
                color: "green",
                title: "Slettet",
                message:
                    `${item.name} blev slettet.`,
            });

        } catch (error) {
            console.error(error);

            notifications.show({
                color: "red",
                title: "Fejl",

                message:
                    error instanceof Error
                        ? error.message
                        : "Produktet kunne ikke slettes.",
            });
        }
    }

    // ---------------------------------------------------------
    // INVALID EVENT
    // ---------------------------------------------------------

    if (
        !Number.isFinite(parsedEventId) ||
        parsedEventId <= 0
    ) {
        return (
            <>
                <header className="pl-topbar">
                    <div>
                        <h1>
                            Indstillinger
                        </h1>

                        <div className="pl-steps">
                            Player Lounge menu
                        </div>
                    </div>
                </header>

                <div className="pl-content">
                    <Card withBorder>
                        <Text c="red">
                            Event ID mangler eller
                            er ugyldigt.
                        </Text>
                    </Card>
                </div>
            </>
        );
    }

    // ---------------------------------------------------------
    // UI
    // ---------------------------------------------------------

    return (
        <>
            <header className="pl-topbar">
                <div>
                    <h1>
                        Indstillinger
                    </h1>

                    <div className="pl-steps">
                        Administrér mad og
                        drikkevarer
                    </div>
                </div>
            </header>

            <div className="pl-content">
                <Card
                    withBorder
                    radius="md"
                    mb="lg"
                >
                    <Stack>
                        <div>
                            <Title order={3}>
                                Køkkenindstillinger
                            </Title>

                            <Text
                                c="dimmed"
                                size="sm"
                            >
                                Indstillinger for beregning af forventet klargøringstid.
                            </Text>
                        </div>

                        <Group align="flex-end">
                            <NumberInput
                                label="Antal mikroovne"
                                description="Hvor mange mikroovne kan bruges samtidig?"
                                min={1}
                                value={
                                    estimateSettings.microwaveCount
                                }
                                onChange={value =>
                                    setEstimateSettings(
                                        prev => ({
                                            ...prev,
                                            microwaveCount:
                                                typeof value ===
                                                    "number"
                                                    ? value
                                                    : 1,
                                        })
                                    )
                                }
                            />

                            <NumberInput
                                label="Minutter pr. boks"
                                description="Hvor lang tid tager én boks?"
                                min={0}
                                value={
                                    estimateSettings.minutesPerBox
                                }
                                onChange={value =>
                                    setEstimateSettings(
                                        prev => ({
                                            ...prev,
                                            minutesPerBox:
                                                typeof value ===
                                                    "number"
                                                    ? value
                                                    : 0,
                                        })
                                    )
                                }
                            />

                            <Button
                                loading={
                                    savingSettings
                                }
                                onClick={
                                    saveEstimateSettings
                                }
                            >
                                Gem indstillinger
                            </Button>
                        </Group>
                    </Stack>
                </Card>
                {/* HEADER */}

                <Group
                    justify="space-between"
                    mb="lg"
                >
                    <div>
                        <Title order={3}>
                            Produkter
                        </Title>

                        <Text
                            c="dimmed"
                            size="sm"
                        >
                            {items.length} produkt
                            {items.length === 1
                                ? ""
                                : "er"}{" "}
                            oprettet
                        </Text>
                    </div>

                    <Group>
                        <Button
                            variant="default"
                            leftSection={
                                <IconRefresh
                                    size={18}
                                />
                            }
                            loading={
                                loading
                            }
                            onClick={
                                loadItems
                            }
                        >
                            Opdater
                        </Button>

                        <Button
                            leftSection={
                                <IconPlus
                                    size={18}
                                />
                            }
                            onClick={
                                openCreate
                            }
                        >
                            Opret produkt
                        </Button>
                    </Group>
                </Group>

                {/* PRODUCTS */}

                <Card
                    withBorder
                    radius="md"
                    padding={0}
                >
                    {loading ? (
                        <Center
                            mih={250}
                        >
                            <Loader />
                        </Center>
                    ) : (
                        <Table
                            striped
                            highlightOnHover
                            verticalSpacing="md"
                            horizontalSpacing="lg"
                        >
                            <Table.Thead>
                                <Table.Tr>
                                    <Table.Th>
                                        Produkt
                                    </Table.Th>

                                    <Table.Th>
                                        Kategori
                                    </Table.Th>

                                    <Table.Th>
                                        Pris
                                    </Table.Th>

                                    <Table.Th>
                                        Sortering
                                    </Table.Th>

                                    <Table.Th>
                                        Status
                                    </Table.Th>

                                    <Table.Th
                                        style={{
                                            width: 140,
                                        }}
                                    >
                                        Handling
                                    </Table.Th>
                                </Table.Tr>
                            </Table.Thead>

                            <Table.Tbody>

                                {items.map(
                                    item => (
                                        <Table.Tr
                                            key={
                                                item.id
                                            }
                                        >
                                            {/* PRODUCT */}

                                            <Table.Td>
                                                <Group
                                                    wrap="nowrap"
                                                >
                                                    {item.imageUrl ? (
                                                        <img
                                                            src={
                                                                item.imageUrl
                                                            }
                                                            alt={
                                                                item.name
                                                            }
                                                            style={{
                                                                width: 58,
                                                                height: 58,
                                                                borderRadius: 9,
                                                                objectFit: "cover",
                                                            }}
                                                        />
                                                    ) : (
                                                        <div
                                                            style={{
                                                                width: 58,
                                                                height: 58,
                                                                borderRadius: 9,
                                                                background: "#f1f3f5",
                                                                display: "flex",
                                                                alignItems: "center",
                                                                justifyContent: "center",
                                                                color: "#868e96",
                                                                fontWeight: 800,
                                                                fontSize: 20,
                                                            }}
                                                        >
                                                            {item.name
                                                                .substring(
                                                                    0,
                                                                    1
                                                                )
                                                                .toUpperCase()}
                                                        </div>
                                                    )}

                                                    <div>
                                                        <Text
                                                            fw={
                                                                600
                                                            }
                                                        >
                                                            {
                                                                item.name
                                                            }
                                                        </Text>

                                                        <Text
                                                            size="xs"
                                                            c="dimmed"
                                                        >
                                                            ID #
                                                            {
                                                                item.id
                                                            }
                                                        </Text>
                                                    </div>
                                                </Group>
                                            </Table.Td>

                                            {/* CATEGORY */}

                                            <Table.Td>
                                                {
                                                    item.category
                                                }
                                            </Table.Td>

                                            {/* PRICE */}

                                            <Table.Td>
                                                <Text
                                                    fw={
                                                        500
                                                    }
                                                >
                                                    {item.price.toLocaleString(
                                                        "da-DK",
                                                        {
                                                            style: "currency",
                                                            currency: "DKK",
                                                            minimumFractionDigits: 0,
                                                        }
                                                    )}
                                                </Text>
                                            </Table.Td>

                                            {/* SORT */}

                                            <Table.Td>
                                                {
                                                    item.sortOrder
                                                }
                                            </Table.Td>

                                            {/* ACTIVE */}

                                            <Table.Td>
                                                <Switch
                                                    checked={
                                                        item.isActive
                                                    }
                                                    onChange={() =>
                                                        toggleActive(
                                                            item
                                                        )
                                                    }
                                                    label={
                                                        item.isActive
                                                            ? "Aktiv"
                                                            : "Inaktiv"
                                                    }
                                                />
                                            </Table.Td>

                                            {/* ACTIONS */}

                                            <Table.Td>
                                                <Group
                                                    gap="xs"
                                                >
                                                    <ActionIcon
                                                        variant="light"
                                                        size="lg"
                                                        title="Rediger"
                                                        onClick={() =>
                                                            openEdit(
                                                                item
                                                            )
                                                        }
                                                    >
                                                        <IconEdit
                                                            size={
                                                                18
                                                            }
                                                        />
                                                    </ActionIcon>

                                                    <ActionIcon
                                                        color="red"
                                                        variant="light"
                                                        size="lg"
                                                        title="Slet"
                                                        onClick={() =>
                                                            deleteProduct(
                                                                item
                                                            )
                                                        }
                                                    >
                                                        <IconTrash
                                                            size={
                                                                18
                                                            }
                                                        />
                                                    </ActionIcon>
                                                </Group>
                                            </Table.Td>
                                        </Table.Tr>
                                    )
                                )}

                                {items.length ===
                                    0 && (
                                        <Table.Tr>
                                            <Table.Td
                                                colSpan={
                                                    6
                                                }
                                            >
                                                <Center
                                                    py={
                                                        60
                                                    }
                                                >
                                                    <Stack
                                                        align="center"
                                                        gap="xs"
                                                    >
                                                        <Text
                                                            fw={
                                                                600
                                                            }
                                                        >
                                                            Ingen
                                                            produkter
                                                            endnu
                                                        </Text>

                                                        <Text
                                                            c="dimmed"
                                                            size="sm"
                                                        >
                                                            Opret det
                                                            første
                                                            produkt til
                                                            Player
                                                            Lounge.
                                                        </Text>

                                                        <Button
                                                            mt="sm"
                                                            leftSection={
                                                                <IconPlus
                                                                    size={
                                                                        18
                                                                    }
                                                                />
                                                            }
                                                            onClick={
                                                                openCreate
                                                            }
                                                        >
                                                            Opret
                                                            produkt
                                                        </Button>
                                                    </Stack>
                                                </Center>
                                            </Table.Td>
                                        </Table.Tr>
                                    )}

                            </Table.Tbody>
                        </Table>
                    )}
                </Card>
            </div>

            {/* CREATE / EDIT */}

            <Modal
                opened={
                    modalOpen
                }
                onClose={() => {
                    setModalOpen(false);
                    setEditingItem(null);
                }}
                title={
                    editingItem
                        ? "Rediger produkt"
                        : "Opret produkt"
                }
                centered
                size="md"
            >
                <Stack>
                    <TextInput
                        label="Produktnavn"
                        placeholder="Fx Gul Thai Box"
                        required
                        value={
                            form.name
                        }
                        onChange={event =>
                            setForm(prev => ({
                                ...prev,

                                name:
                                    event
                                        .currentTarget
                                        .value,
                            }))
                        }
                    />

                    <Select
                        label="Kategori"
                        data={[
                            "Mad",
                            "Drikke",
                            "Dessert",
                            "Snack",
                        ]}
                        searchable
                        allowDeselect={
                            false
                        }
                        value={
                            form.category
                        }
                        onChange={value =>
                            setForm(prev => ({
                                ...prev,

                                category:
                                    value ??
                                    "Mad",
                            }))
                        }
                    />

                    <NumberInput
                        label="Pris"
                        suffix=" kr."
                        min={0}
                        decimalScale={2}
                        value={
                            form.price
                        }
                        onChange={value =>
                            setForm(prev => ({
                                ...prev,

                                price:
                                    typeof value ===
                                        "number"
                                        ? value
                                        : 0,
                            }))
                        }
                    />

                    <NumberInput
                        label="Sortering"
                        description="Laveste nummer vises først"
                        min={0}
                        value={
                            form.sortOrder
                        }
                        onChange={value =>
                            setForm(prev => ({
                                ...prev,

                                sortOrder:
                                    typeof value ===
                                        "number"
                                        ? value
                                        : 0,
                            }))
                        }
                    />

                    <TextInput
                        label="Billede URL"
                        placeholder="/uploads/food/thai-yellow.jpg"
                        value={
                            form.imageUrl
                        }
                        onChange={event =>
                            setForm(prev => ({
                                ...prev,

                                imageUrl:
                                    event
                                        .currentTarget
                                        .value,
                            }))
                        }
                    />

                    {form.imageUrl && (
                        <div>
                            <Text
                                fw={500}
                                size="sm"
                                mb={6}
                            >
                                Forhåndsvisning
                            </Text>

                            <img
                                src={
                                    form.imageUrl
                                }
                                alt={
                                    form.name ||
                                    "Produkt"
                                }
                                style={{
                                    width: "100%",
                                    height: 190,
                                    objectFit: "cover",
                                    borderRadius: 10,
                                    border: "1px solid #dee2e6",
                                }}
                            />
                        </div>
                    )}

                    {editingItem && (
                        <Switch
                            label="Produktet er aktivt"
                            description="Inaktive produkter vises ikke på bestillingssiden."
                            checked={
                                form.isActive
                            }
                            onChange={event =>
                                setForm(prev => ({
                                    ...prev,

                                    isActive:
                                        event
                                            .currentTarget
                                            .checked,
                                }))
                            }
                        />
                    )}

                    <Group
                        justify="flex-end"
                        mt="md"
                    >
                        <Button
                            variant="default"
                            disabled={
                                saving
                            }
                            onClick={() => {
                                setModalOpen(
                                    false
                                );

                                setEditingItem(
                                    null
                                );
                            }}
                        >
                            Annuller
                        </Button>

                        <Button
                            loading={
                                saving
                            }
                            disabled={
                                !form.name.trim()
                            }
                            onClick={
                                saveProduct
                            }
                        >
                            {editingItem
                                ? "Gem ændringer"
                                : "Opret produkt"}
                        </Button>
                    </Group>
                </Stack>
            </Modal>
        </>
    );
}